import { Device, FacilityEvent } from "@app/shared/src/models";

/**
 * Why a report has no radar track, in words a reader can act on.
 *
 * The server holds an event's track only once the unit has uploaded it, and the unit uploads the
 * pieces of an incident on three separate streams: the event goes out the moment the detector
 * fires and the photo with it, but the track is queued only when the tracker lets the vehicle go —
 * seconds later — and sent by its own stream after that. So "no track" is several different
 * situations, and the report used to describe every one of them as the server having let the
 * track go, which it never does:
 *
 * - "offline": the unit went quiet right after the event and has not checked in since. The track
 *   is still on the unit and uploads when it reconnects. (QC Truck, 2026-10-27: the event and its
 *   photo arrived five seconds after the last heartbeat, then the unit lost DNS for the night.)
 * - "uploading": the unit is in touch, but the event is recent enough that the track may still be
 *   on its way.
 * - "unreported": the unit stayed in touch well past the event and never sent a track. The
 *   tracker reports only the tracks that pass its own filters — long enough, not a duplicate of
 *   another track, not a rain phantom — while the alert detectors fire on every track it has, so a
 *   track can be good enough to alarm on and still not good enough to keep. Nothing will arrive.
 * - "unknown": no device record to judge by, so nothing beyond "there is no track" can be said.
 */
export type MissingTrackReason = "offline" | "uploading" | "unreported" | "unknown";

/**
 * How long after an event a unit must have stayed in touch before its silence on the track means
 * anything. The track is queued when the tracker drops the vehicle, which for a pass or a near miss
 * is seconds, and the sync stream posts it within a minute or so. Five minutes is generous enough
 * that a unit still in touch at the end of it has had every chance.
 */
export const TRACK_UPLOAD_GRACE_MS = 5 * 60 * 1000;

/** A unit silent this long is offline — the same line DeviceStatusTag draws. */
export const OFFLINE_AFTER_MS = 15 * 60 * 1000;

/** The fields the reading depends on — the generated classes carry Dates, the raw JSON strings. */
type EventLike = Pick<Partial<FacilityEvent>, "dateCreated"> | { dateCreated?: Date | string };
type DeviceLike = Pick<Partial<Device>, "lastHeartbeat"> | { lastHeartbeat?: Date | string };

export function missingTrackReason(event: EventLike, device: DeviceLike | undefined, now = Date.now()): MissingTrackReason {
    const eventMs = event.dateCreated ? new Date(event.dateCreated).getTime() : NaN;
    const heardMs = device?.lastHeartbeat ? new Date(device.lastHeartbeat).getTime() : NaN;
    if (Number.isNaN(eventMs) || Number.isNaN(heardMs)) return "unknown";
    if (heardMs >= eventMs + TRACK_UPLOAD_GRACE_MS) return "unreported";
    return now - heardMs > OFFLINE_AFTER_MS ? "offline" : "uploading";
}

/** The report's note where the path and speed profile would be. */
export function missingTrackNote(reason: MissingTrackReason): string {
    switch (reason) {
        case "offline":
            return "The unit went quiet right after this event and hasn't checked in since, so its radar track is still on the unit. The path, speed profile and clip will fill in here once it reconnects.";
        case "uploading":
            return "The unit is still uploading the radar track for this event. Check back in a few minutes for the path and speed profile.";
        case "unreported":
            return "The unit never reported a radar track for this event, so the path and speed profile are unavailable. That usually means the track was too short or too uncertain for the unit to keep.";
        default:
            return "There is no radar track on the server for this event, so the path and speed profile are unavailable.";
    }
}

/** The clip card's line when there is no track to cut a clip around. */
export function missingTrackClipNote(reason: MissingTrackReason): string {
    switch (reason) {
        case "offline":
            return "No clip was pulled for this event, and its radar track is still on the unit, which hasn't checked in since. A clip can be cut around the track once it arrives.";
        case "uploading":
            return "No clip was pulled for this event. Once the unit finishes uploading the radar track, a clip can be cut around it.";
        default:
            return "No clip was pulled for this event, and there is no radar track on the server to cut a clip around.";
    }
}
